import { useInifiniteDatas } from "@/hooks/useInfiniteDatas";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import styled from "@emotion/styled";
import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { getQuestions } from "../../remotes/getQuestions";
import { getQuestionsSearch } from "../../remotes/getQuestionsSearch";
import { InquiryDrawer } from "../InquiryDrawer";
import { NoResult } from "../NoResult";
import { SearchResultCount } from "../SearchResultCount";
import { customEvent, Tags, VStack } from "goi_common";
import { GuidebookInquiry } from "./GuidebookInquiry";

interface GuidebookInquiriesProps {
  keyword?: string;
}
export function GuidebookInquiries({ keyword }: GuidebookInquiriesProps) {
  const router = useRouter();

  const [tag, setTag] = useState<string | undefined>(undefined);
  const id = useMemo(() => {
    if (!router.isReady) return;
    if (router.query.id == null) return;
    return Number(router.query.id);
  }, [router.isReady, router.query.id]);

  const handleClose = useCallback(() => {
    router.push("/guidebook?tab=qna");
  }, [router]);

  const { data: questionPages, ...question } = useInfiniteQuery(
    ["questions", tag],
    getQuestions(tag),
    {
      enabled: keyword == null,
      getNextPageParam: (lastPage) => lastPage.next,
    },
  );
  const { datas: questions } = useInifiniteDatas(questionPages);

  const { data: questionSearchPages, ...search } = useInfiniteQuery(
    ["questions-search", keyword],
    getQuestionsSearch(keyword),
    {
      enabled: keyword != null,
      getNextPageParam: (lastPage) => lastPage.next,
      refetchOnMount: false,
    },
  );
  const { datas: questionsSearch, count } = useInifiniteDatas(questionSearchPages);

  const { setTarget: setQuestionTarget } = useInfiniteScroll(question.fetchNextPage);
  const { setTarget: setSearchTarget } = useInfiniteScroll(search.fetchNextPage);

  return (
    <>
      <GuidebookInquiriesRoot gap={keyword ? 24 : 30}>
        {keyword ? (
          <SearchResultCount count={count} />
        ) : (
          <Tags
            tags={TAGS}
            value={tag}
            onChange={(v) => {
              customEvent({
                newGtm: {
                  path: "guidebook",
                  trigger_and_target: "chip_qna-qna_type_text",
                  text: tag,
                },
              });
              setTag(v);
            }}
            padding={24}
          />
        )}
        <VStack gap={16} padding="0 24px">
          {(keyword ? questionsSearch : questions)?.map((question) => {
            return (
              <GuidebookInquiry
                key={question.id}
                id={question.id}
                author={question.author}
                content={question.content}
                status={question.status}
                tags={question.tags}
                keyword={keyword}
              />
            );
          })}
          {keyword && questionsSearch?.length === 0 && <NoResult keyword={keyword} />}
          {questions && question.hasNextPage && !question.isFetchingNextPage && (
            <div ref={setQuestionTarget} />
          )}
          {questionsSearch && search.hasNextPage && !search.isFetchingNextPage && (
            <div ref={setSearchTarget} />
          )}
        </VStack>
      </GuidebookInquiriesRoot>
      <InquiryDrawer id={id} isOpen={id != null} onClose={handleClose} />
    </>
  );
}

const TAGS = [
  { key: "전체", value: undefined },
  { key: "장례 비용", value: "장례 비용" },
  { key: "장례 절차", value: "장례 절차" },
  { key: "장례식장", value: "장례식장" },
  { key: "봉안당", value: "봉안당" },
  { key: "수목장", value: "수목장" },
  { key: "안치 시설", value: "안치 시설" },
  { key: "이장·개장", value: "이장·개장" },
  { key: "무빈소장", value: "무빈소장" },
  { key: "가족장", value: "가족장" },
  { key: "일반장", value: "일반장" },
  { key: "상조회사", value: "상조회사" },
  { key: "기타", value: "기타" },
];

const GuidebookInquiriesRoot = styled(VStack)`
  padding: 24px 0;
`;
